import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format as formatDate, parseISO } from 'date-fns';
import {
  loyaltyCampaignService,
  type LoyaltyCampaign,
  type LoyaltyCampaignType,
  type LoyaltyCampaignConfig,
  type OrderCountCampaignConfig,
  type WeeklyStreakCampaignConfig,
  type InactivityCampaignConfig,
  type LoyaltyRewardType,
} from '../services';
import authService from '../services/authService';
import { useToast } from '../hooks/useToast';

type CampaignTypeFilter = 'all' | LoyaltyCampaignType;
type StatusFilter = 'all' | 'active' | 'inactive';

interface CampaignFormState {
  type: LoyaltyCampaignType;
  name: string;
  description: string;
  triggerHour: string;
  active: boolean;
  config: {
    ordersRequired?: string;
    rewardType?: LoyaltyRewardType;
    rewardValue?: string;
    couponValue?: string;
    couponMinOrderValue?: string;
    rewardValidityDays?: string;
    ordersWindowDays?: string;
    messageTemplateId?: string;
    messageTemplateBody?: string;
    rewardDescription?: string;
    requiredWeeks?: string;
    bonusItemDescription?: string;
    thresholdDays?: string;
    couponValidityDays?: string;
  };
}

const TYPE_LABELS: Record<LoyaltyCampaignType, string> = {
  ORDER_COUNT: 'Contagem de pedidos',
  WEEKLY_STREAK: 'Sequência semanal',
  INACTIVITY: 'Reativação por inatividade',
};

const REWARD_TYPE_LABELS: Record<LoyaltyRewardType, string> = {
  COUPON: 'Cupom',
  ITEM: 'Item',
  CUSTOM: 'Personalizado',
};

const PRIMARY_BUTTON_SX = {
  backgroundColor: '#F2C94C',
  color: '#2F2A1D',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#E0B83D',
    boxShadow: 'none',
  },
};

const createEmptyForm = (type: LoyaltyCampaignType): CampaignFormState => ({
  type,
  name: '',
  description: '',
  triggerHour: '',
  active: false,
  config: {
    rewardType: 'COUPON',
  },
});

const formatDateTime = (value?: string | Date) => {
  if (!value) return '-';
  try {
    const date = typeof value === 'string' ? parseISO(value) : value;
    return formatDate(date, 'dd/MM/yyyy HH:mm');
  } catch (err) {
    return typeof value === 'string' ? value : value.toISOString();
  }
};

const normalizeNumber = (value?: string) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeInteger = (value?: string) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const buildConfigFromForm = (form: CampaignFormState): LoyaltyCampaignConfig => {
  switch (form.type) {
    case 'ORDER_COUNT': {
      const ordersRequired = normalizeInteger(form.config.ordersRequired);
      if (!ordersRequired || ordersRequired <= 0) {
        throw new Error('Informe o número mínimo de pedidos (inteiro positivo).');
      }
      const rewardType = form.config.rewardType ?? 'COUPON';
      const couponValue = normalizeNumber(form.config.couponValue);
      if (rewardType === 'COUPON' && (!couponValue || couponValue <= 0)) {
        throw new Error('Informe o valor do cupom para recompensas do tipo cupom.');
      }
      return {
        ordersRequired,
        rewardType,
        rewardValue: form.config.rewardValue?.trim() || undefined,
        couponValue,
        couponMinOrderValue: normalizeNumber(form.config.couponMinOrderValue),
        rewardValidityDays: normalizeInteger(form.config.rewardValidityDays),
        ordersWindowDays: normalizeInteger(form.config.ordersWindowDays),
        messageTemplateId: form.config.messageTemplateId?.trim() || undefined,
        messageTemplateBody: form.config.messageTemplateBody?.trim() || undefined,
        rewardDescription: form.config.rewardDescription?.trim() || undefined,
      } satisfies OrderCountCampaignConfig;
    }
    case 'WEEKLY_STREAK': {
      const requiredWeeks = normalizeInteger(form.config.requiredWeeks);
      if (!requiredWeeks || requiredWeeks <= 0) {
        throw new Error('Informe o número de semanas consecutivas (inteiro positivo).');
      }
      const couponValue = normalizeNumber(form.config.couponValue);
      const bonusItemDescription = form.config.bonusItemDescription?.trim();
      if (!couponValue && !bonusItemDescription) {
        throw new Error('Informe o valor do cupom ou uma descrição de item bônus.');
      }
      return {
        requiredWeeks,
        bonusItemDescription: bonusItemDescription || undefined,
        couponValue: couponValue || undefined,
        couponMinOrderValue: normalizeNumber(form.config.couponMinOrderValue),
        rewardValidityDays: normalizeInteger(form.config.rewardValidityDays),
        messageTemplateId: form.config.messageTemplateId?.trim() || undefined,
        messageTemplateBody: form.config.messageTemplateBody?.trim() || undefined,
        rewardDescription: form.config.rewardDescription?.trim() || undefined,
      } satisfies WeeklyStreakCampaignConfig;
    }
    case 'INACTIVITY': {
      const thresholdDays = normalizeInteger(form.config.thresholdDays);
      if (!thresholdDays || thresholdDays <= 0) {
        throw new Error('Informe o número de dias de inatividade (inteiro positivo).');
      }
      const couponValue = normalizeNumber(form.config.couponValue);
      if (!couponValue || couponValue <= 0) {
        throw new Error('Informe o valor do cupom de reativação.');
      }
      const couponValidityDays = normalizeInteger(form.config.couponValidityDays);
      if (!couponValidityDays || couponValidityDays <= 0) {
        throw new Error('Informe a validade do cupom (dias, inteiro positivo).');
      }
      return {
        thresholdDays,
        couponValue,
        couponMinOrderValue: normalizeNumber(form.config.couponMinOrderValue),
        couponValidityDays,
        messageTemplateId: form.config.messageTemplateId?.trim() || undefined,
        messageTemplateBody: form.config.messageTemplateBody?.trim() || undefined,
        rewardDescription: form.config.rewardDescription?.trim() || undefined,
      } satisfies InactivityCampaignConfig;
    }
    default:
      throw new Error('Tipo de campanha inválido.');
  }
};

const buildFormStateFromCampaign = (campaign: LoyaltyCampaign): CampaignFormState => {
  const base: CampaignFormState = {
    type: campaign.type,
    name: campaign.name,
    description: campaign.description ?? '',
    triggerHour: campaign.triggerHour ?? '',
    active: campaign.active,
    config: {},
  };

  switch (campaign.type) {
    case 'ORDER_COUNT': {
      const config = campaign.config as OrderCountCampaignConfig;
      base.config = {
        ordersRequired: String(config.ordersRequired ?? ''),
        rewardType: config.rewardType,
        rewardValue: config.rewardValue ?? '',
        couponValue: config.couponValue !== undefined ? String(config.couponValue) : '',
        couponMinOrderValue:
          config.couponMinOrderValue !== undefined ? String(config.couponMinOrderValue) : '',
        rewardValidityDays: config.rewardValidityDays !== undefined ? String(config.rewardValidityDays) : '',
        ordersWindowDays: config.ordersWindowDays !== undefined ? String(config.ordersWindowDays) : '',
        messageTemplateId: config.messageTemplateId ?? '',
        messageTemplateBody: config.messageTemplateBody ?? '',
        rewardDescription: config.rewardDescription ?? '',
      };
      break;
    }
    case 'WEEKLY_STREAK': {
      const config = campaign.config as WeeklyStreakCampaignConfig;
      base.config = {
        requiredWeeks: String(config.requiredWeeks ?? ''),
        bonusItemDescription: config.bonusItemDescription ?? '',
        couponValue: config.couponValue !== undefined ? String(config.couponValue) : '',
        couponMinOrderValue:
          config.couponMinOrderValue !== undefined ? String(config.couponMinOrderValue) : '',
        rewardValidityDays: config.rewardValidityDays !== undefined ? String(config.rewardValidityDays) : '',
        messageTemplateId: config.messageTemplateId ?? '',
        messageTemplateBody: config.messageTemplateBody ?? '',
        rewardDescription: config.rewardDescription ?? '',
      };
      break;
    }
    case 'INACTIVITY': {
      const config = campaign.config as InactivityCampaignConfig;
      base.config = {
        thresholdDays: String(config.thresholdDays ?? ''),
        couponValue: config.couponValue !== undefined ? String(config.couponValue) : '',
        couponMinOrderValue:
          config.couponMinOrderValue !== undefined ? String(config.couponMinOrderValue) : '',
        couponValidityDays: config.couponValidityDays !== undefined ? String(config.couponValidityDays) : '',
        messageTemplateId: config.messageTemplateId ?? '',
        messageTemplateBody: config.messageTemplateBody ?? '',
        rewardDescription: config.rewardDescription ?? '',
      };
      break;
    }
    default:
      break;
  }

  return base;
};

const summarizeConfig = (campaign: LoyaltyCampaign): string => {
  switch (campaign.type) {
    case 'ORDER_COUNT': {
      const config = campaign.config as OrderCountCampaignConfig;
      const reward = config.rewardType === 'COUPON'
        ? `Cupom ${config.couponValue ? `R$ ${config.couponValue.toFixed(2)}` : ''}`
        : REWARD_TYPE_LABELS[config.rewardType];
      return `A cada ${config.ordersRequired} pedidos → ${reward}`;
    }
    case 'WEEKLY_STREAK': {
      const config = campaign.config as WeeklyStreakCampaignConfig;
      const rewardParts: string[] = [];
      if (config.bonusItemDescription) rewardParts.push(config.bonusItemDescription);
      if (config.couponValue) rewardParts.push(`Cupom R$ ${config.couponValue.toFixed(2)}`);
      return `${config.requiredWeeks} semanas seguidas → ${rewardParts.join(' + ')}`;
    }
    case 'INACTIVITY': {
      const config = campaign.config as InactivityCampaignConfig;
      return `${config.thresholdDays} dias inativo → Cupom R$ ${config.couponValue.toFixed(2)}`;
    }
    default:
      return '';
  }
};

const LoyaltyCampaignsPage: React.FC = () => {
  const { showError, showSuccess, showInfo } = useToast();
  const userRole = authService.getUserRole();
  const isDeveloper = userRole === 'DEVELOPER';
  const defaultCnpj = authService.getActiveCompanyCnpj() ?? authService.getUser()?.cnpj ?? '';

  const [campaigns, setCampaigns] = useState<LoyaltyCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<CampaignTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [cnpjFilter, setCnpjFilter] = useState(isDeveloper ? '' : defaultCnpj);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [formState, setFormState] = useState<CampaignFormState>(createEmptyForm('ORDER_COUNT'));
  const [editingCampaign, setEditingCampaign] = useState<LoyaltyCampaign | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<LoyaltyCampaign | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const sanitizedCnpj = useMemo(() => {
    if (!cnpjFilter) return undefined;
    const digits = cnpjFilter.replace(/\D/g, '');
    return digits.length > 0 ? digits : undefined;
  }, [cnpjFilter]);

  const loadCampaigns = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const data = await loyaltyCampaignService.listCampaigns({
        type: typeFilter === 'all' ? undefined : typeFilter,
        activeOnly: statusFilter === 'active' ? true : undefined,
        cnpj: sanitizedCnpj,
      });
      setCampaigns(data);
    } catch (error: any) {
      console.error('[LoyaltyCampaignsPage] loadCampaigns error', error);
      const message = error?.message || 'Não foi possível carregar as campanhas.';
      setErrorMessage(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  }, [typeFilter, statusFilter, sanitizedCnpj, showError]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      if (statusFilter === 'inactive' && campaign.active) {
        return false;
      }
      return true;
    });
  }, [campaigns, statusFilter]);

  const handleOpenDialog = (campaign?: LoyaltyCampaign) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormState(buildFormStateFromCampaign(campaign));
    } else {
      setEditingCampaign(null);
      setFormState(createEmptyForm('ORDER_COUNT'));
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (dialogLoading) return;
    setDialogOpen(false);
    setEditingCampaign(null);
    setFormState(createEmptyForm('ORDER_COUNT'));
  };

  const handleFormChange = <K extends keyof CampaignFormState>(key: K, value: CampaignFormState[K]) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleConfigChange = (key: keyof CampaignFormState['config'], value: string) => {
    setFormState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [key]: value,
      },
    }));
  };

  const handleChangeType = (type: LoyaltyCampaignType) => {
    setFormState((prev) => ({
      ...createEmptyForm(type),
      name: prev.name,
      description: prev.description,
      triggerHour: prev.triggerHour,
      active: prev.active,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState.name.trim()) {
      showInfo('Informe o nome da campanha.');
      return;
    }

    try {
      setDialogLoading(true);
      const config = buildConfigFromForm(formState);
      const payload = {
        name: formState.name.trim(),
        description: formState.description.trim() || undefined,
        triggerHour: formState.triggerHour || undefined,
        active: formState.active,
        config,
      };

      if (editingCampaign) {
        const updated = await loyaltyCampaignService.updateCampaign(editingCampaign.id, payload, {
          cnpj: sanitizedCnpj,
        });
        showSuccess('Campanha atualizada com sucesso.');
        setCampaigns((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await loyaltyCampaignService.createCampaign(
          {
            type: formState.type,
            ...payload,
          },
          { cnpj: sanitizedCnpj },
        );
        showSuccess('Campanha criada com sucesso.');
        setCampaigns((prev) => [created, ...prev]);
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('[LoyaltyCampaignsPage] handleSubmit error', error);
      const message = error?.message || 'Não foi possível salvar a campanha.';
      showError(message);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await loyaltyCampaignService.deleteCampaign(deleteTarget.id, { cnpj: sanitizedCnpj });
      showSuccess('Campanha removida com sucesso.');
      setCampaigns((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error: any) {
      console.error('[LoyaltyCampaignsPage] handleDeleteCampaign error', error);
      const message = error?.message || 'Não foi possível remover a campanha.';
      showError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderConfigFields = () => {
    switch (formState.type) {
      case 'ORDER_COUNT':
        return (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Pedidos necessários"
                value={formState.config.ordersRequired || ''}
                onChange={(event) => handleConfigChange('ordersRequired', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
                required
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '33%' }, flexGrow: 1, minWidth: { md: 200 } }}
              />
              <FormControl fullWidth size="small" sx={{ flexBasis: { xs: '100%', md: '67%' }, flexGrow: 1, minWidth: { md: 260 } }}>
                <InputLabel id="reward-type-label">Tipo de recompensa</InputLabel>
                <Select
                  labelId="reward-type-label"
                  label="Tipo de recompensa"
                  value={formState.config.rewardType || 'COUPON'}
                  onChange={(event) => handleConfigChange('rewardType', event.target.value as LoyaltyRewardType)}
                >
                  {Object.entries(REWARD_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {formState.config.rewardType === 'COUPON' ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Valor do cupom (R$)"
                  value={formState.config.couponValue || ''}
                  onChange={(event) => handleConfigChange('couponValue', event.target.value)}
                  fullWidth
                  inputProps={{ inputMode: 'decimal', min: 0 }}
                  required
                  size="small"
                  sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 220 } }}
                />
              </Stack>
            ) : (
              <TextField
                label="Descrição da recompensa"
                value={formState.config.rewardValue || ''}
                onChange={(event) => handleConfigChange('rewardValue', event.target.value)}
                fullWidth
                size="small"
              />
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Pedido mínimo para usar cupom (R$)"
                value={formState.config.couponMinOrderValue || ''}
                onChange={(event) => handleConfigChange('couponMinOrderValue', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'decimal', min: 0 }}
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 220 } }}
              />
              <TextField
                label="Validade do cupom (dias)"
                value={formState.config.couponValidityDays || ''}
                onChange={(event) => handleConfigChange('couponValidityDays', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'numeric', min: 1 }}
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 220 } }}
              />
            </Stack>

            <TextField
              label="Janela de pedidos (dias)"
              helperText="Opcional — quantidade de dias para considerar pedidos na contagem"
              value={formState.config.ordersWindowDays || ''}
              onChange={(event) => handleConfigChange('ordersWindowDays', event.target.value)}
              fullWidth
              inputProps={{ inputMode: 'numeric', min: 1 }}
              size="small"
            />
          </Stack>
        );
      case 'WEEKLY_STREAK':
        return (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Semanas consecutivas"
                value={formState.config.requiredWeeks || ''}
                onChange={(event) => handleConfigChange('requiredWeeks', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'numeric', min: 1 }}
                required
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '33%' }, flexGrow: 1, minWidth: { md: 200 } }}
              />
              <TextField
                label="Descrição do item bônus"
                helperText="Informe uma descrição ou deixe vazio se for cupom"
                value={formState.config.bonusItemDescription || ''}
                onChange={(event) => handleConfigChange('bonusItemDescription', event.target.value)}
                fullWidth
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '67%' }, flexGrow: 1, minWidth: { md: 260 } }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Valor do cupom (R$)"
                helperText="Informe um valor ou deixe vazio se for apenas item bônus"
                value={formState.config.couponValue || ''}
                onChange={(event) => handleConfigChange('couponValue', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'decimal', min: 0 }}
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 220 } }}
              />
              <TextField
                label="Pedido mínimo para usar cupom (R$)"
                value={formState.config.couponMinOrderValue || ''}
                onChange={(event) => handleConfigChange('couponMinOrderValue', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'decimal', min: 0 }}
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 220 } }}
              />
            </Stack>

            <TextField
              label="Validade da recompensa (dias)"
              value={formState.config.rewardValidityDays || ''}
              onChange={(event) => handleConfigChange('rewardValidityDays', event.target.value)}
              fullWidth
              inputProps={{ inputMode: 'numeric', min: 1 }}
              size="small"
            />
          </Stack>
        );
      case 'INACTIVITY':
        return (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Dias de inatividade"
                value={formState.config.thresholdDays || ''}
                onChange={(event) => handleConfigChange('thresholdDays', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'numeric', min: 1 }}
                required
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '33%' }, flexGrow: 1, minWidth: { md: 200 } }}
              />
              <TextField
                label="Valor do cupom (R$)"
                value={formState.config.couponValue || ''}
                onChange={(event) => handleConfigChange('couponValue', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'decimal', min: 0 }}
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '67%' }, flexGrow: 1, minWidth: { md: 260 } }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Pedido mínimo para usar cupom (R$)"
                value={formState.config.couponMinOrderValue || ''}
                onChange={(event) => handleConfigChange('couponMinOrderValue', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'decimal', min: 0 }}
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 220 } }}
              />
              <TextField
                label="Validade do cupom (dias)"
                value={formState.config.couponValidityDays || ''}
                onChange={(event) => handleConfigChange('couponValidityDays', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'numeric', min: 1 }}
                size="small"
                sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 220 } }}
              />
            </Stack>
          </Stack>
        );
      default:
        return null;
    }
  };

  const renderSharedMessageFields = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="Descrição da recompensa"
          value={formState.config.rewardDescription || ''}
          onChange={(event) => handleConfigChange('rewardDescription', event.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
          sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 240 } }}
        />
        <TextField
          label="Template WhatsApp (ID)"
          value={formState.config.messageTemplateId || ''}
          onChange={(event) => handleConfigChange('messageTemplateId', event.target.value)}
          fullWidth
          size="small"
          sx={{ flexBasis: { xs: '100%', md: '50%' }, flexGrow: 1, minWidth: { md: 240 } }}
        />
      </Stack>
      <TextField
        label="Mensagem personalizada"
        value={formState.config.messageTemplateBody || ''}
        onChange={(event) => handleConfigChange('messageTemplateBody', event.target.value)}
        fullWidth
        multiline
        minRows={3}
        size="small"
      />
    </Stack>
  );

  return (
    <Box sx={{ backgroundColor: '#F5F6FA', minHeight: '100vh', py: { xs: 2, md: 3 } }}>
      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Campanhas de Fidelidade
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure regras de fidelidade, automatize mensagens e acompanhe o engajamento dos clientes.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ ...PRIMARY_BUTTON_SX, alignSelf: { xs: 'stretch', md: 'auto' } }}
          >
            Nova campanha
          </Button>
        </Stack>

        <Paper
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: '1px solid #E4E7EC',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
            <Box sx={{ flexBasis: { xs: '100%', md: '25%' }, flexGrow: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="type-filter-label">Tipo</InputLabel>
                <Select
                  labelId="type-filter-label"
                  label="Tipo"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value as CampaignTypeFilter)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flexBasis: { xs: '100%', md: '25%' }, flexGrow: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Ativas</MenuItem>
                  <MenuItem value="inactive">Inativas</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {isDeveloper && (
              <Box sx={{ flexBasis: { xs: '100%', md: '30%' }, flexGrow: 1 }}>
                <TextField
                  label="CNPJ"
                  value={cnpjFilter}
                  onChange={(event) => setCnpjFilter(event.target.value)}
                  placeholder="Somente números"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Stack>
        </Paper>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Paper sx={{ mt: 3, borderRadius: 2, border: '1px solid #E4E7EC', backgroundColor: '#FFFFFF' }}>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Resumo</TableCell>
                  <TableCell>Disparo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Atualizado em</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : filteredCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma campanha encontrada para os filtros selecionados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{campaign.name}</Typography>
                        {campaign.description && (
                          <Typography variant="body2" color="text.secondary">
                            {campaign.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{TYPE_LABELS[campaign.type]}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{summarizeConfig(campaign)}</Typography>
                        {campaign.config?.rewardDescription && (
                          <Typography variant="caption" color="text.secondary">
                            {campaign.config.rewardDescription}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{campaign.triggerHour || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.active ? 'Ativa' : 'Inativa'}
                          color={campaign.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDateTime(campaign.updatedAt)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenDialog(campaign)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(campaign)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingCampaign ? 'Editar campanha' : 'Nova campanha'}</DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: '#F7F8FC' }}>
            {!editingCampaign && (
              <FormControl fullWidth sx={{ mb: 3 }} size="small">
                <InputLabel id="campaign-type-label">Tipo de campanha</InputLabel>
                <Select
                  labelId="campaign-type-label"
                  label="Tipo de campanha"
                  value={formState.type}
                  onChange={(event) => handleChangeType(event.target.value as LoyaltyCampaignType)}
                >
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {editingCampaign && (
              <TextField
                label="Tipo de campanha"
                value={TYPE_LABELS[formState.type]}
                fullWidth
                disabled
                sx={{ mb: 3 }}
              />
            )}

            <TextField
              label="Nome da campanha"
              value={formState.name}
              onChange={(event) => handleFormChange('name', event.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <TextField
              label="Descrição"
              value={formState.description}
              onChange={(event) => handleFormChange('description', event.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              flexWrap="wrap"
              alignItems="center"
            >
              <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                <TextField
                  label="Horário de disparo"
                  type="time"
                  value={formState.triggerHour}
                  onChange={(event) => handleFormChange('triggerHour', event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.active}
                      onChange={(event) => handleFormChange('active', event.target.checked)}
                    />
                  }
                  label="Campanha ativa"
                />
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }}>Regras da campanha</Divider>

            {renderConfigFields()}
            {renderSharedMessageFields()}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#F7F8FC', justifyContent: 'space-between' }}>
            <Button onClick={handleCloseDialog} disabled={dialogLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={dialogLoading} sx={PRIMARY_BUTTON_SX}>
              {dialogLoading ? <CircularProgress size={20} /> : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <DialogTitle>Remover campanha</DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#F7F8FC' }}>
          <Typography>
            Tem certeza que deseja remover a campanha{' '}
            <strong>{deleteTarget?.name}</strong>? Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#F7F8FC' }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleDeleteCampaign()}
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  </Box>
  );
};

export default LoyaltyCampaignsPage;
